/**
 * RunReportModal.jsx — pop a real BIP report from the indexed catalog and execute it.
 *
 * Flow:
 *   1. Mount → GET /api/reports/:id/params → render a form per parameter type.
 *   2. User picks format + fills params → POST /api/reports/:id/run.
 *   3. Display result inline (CSV table, HTML, XML/text) or as a download (PDF/XLSX).
 *
 * Notes on parameter rendering:
 *   • If a param has `options[]` and is multi-value, render a <select multiple>.
 *   • If a param has `options[]` and is single-value, render a <select>.
 *   • dataType=DATE → <input type="date"> (BIP accepts YYYY-MM-DD).
 *   • dataType=INTEGER → <input type="number">
 *   • Everything else → <input type="text">.
 *
 * The component is deliberately self-contained — no global state, no router.
 * Pass it `report` (a row from the catalog list) and an `onClose` callback.
 */
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Loader2,
  X,
  Play,
  Download,
  AlertCircle,
  FileSpreadsheet,
  FileText,
  CheckCircle2,
} from 'lucide-react';
import { getApiBase } from '../apiBase';

const API = axios.create({
  baseURL: getApiBase(),
  withCredentials: true,
  timeout: 180000,
});

const FORMATS = [
  { id: 'pdf', label: 'PDF', preview: false },
  { id: 'html', label: 'HTML', preview: true },
  { id: 'xlsx', label: 'Excel (.xlsx)', preview: false },
  { id: 'csv', label: 'CSV (table)', preview: true },
  { id: 'rtf', label: 'RTF', preview: false },
  { id: 'xml', label: 'XML', preview: true },
];

// Map our UI format ids → the BIP keywords getReportDefinition returns.
// One UI option can match several BIP keywords (e.g. Excel covers excel/excel2007/xlsx).
const FORMAT_KEYWORD_ALIASES = {
  pdf: ['pdf'],
  html: ['html', 'mhtml'],
  xlsx: ['excel', 'excel2007', 'excel2000', 'xlsx', 'xls'],
  csv: ['csv'],
  rtf: ['rtf'],
  xml: ['xml', 'data'],
};

function fmtSize(bytes) {
  if (!Number.isFinite(bytes) || bytes <= 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

// Lightweight CSV → array-of-arrays. Handles quoted fields with embedded commas/newlines.
// Not a strict RFC 4180 parser, but good enough to preview BIP exports.
function parseCsv(text) {
  const rows = [];
  let cur = [];
  let field = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"' && text[i + 1] === '"') { field += '"'; i++; }
      else if (ch === '"') { inQuotes = false; }
      else { field += ch; }
    } else {
      if (ch === '"') { inQuotes = true; }
      else if (ch === ',') { cur.push(field); field = ''; }
      else if (ch === '\n') { cur.push(field); rows.push(cur); cur = []; field = ''; }
      else if (ch === '\r') { /* swallow */ }
      else { field += ch; }
    }
  }
  if (field.length > 0 || cur.length > 0) { cur.push(field); rows.push(cur); }
  return rows;
}

function base64ToBlob(b64, mimeType) {
  const bin = atob(b64);
  const len = bin.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: mimeType || 'application/octet-stream' });
}

function ParamField({ p, value, onChange }) {
  const common = 'w-full rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-sm text-slate-800 focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500';

  if (Array.isArray(p.options) && p.options.length > 0) {
    if (p.multiValuesAllowed) {
      const arr = Array.isArray(value) ? value : value ? [value] : [];
      return (
        <select
          multiple
          className={`${common} min-h-[6rem]`}
          value={arr}
          onChange={(e) => {
            const picked = [...e.target.selectedOptions].map((o) => o.value);
            onChange(picked);
          }}
        >
          {p.options.map((opt, i) => (
            <option key={`${opt}-${i}`} value={opt}>{opt}</option>
          ))}
        </select>
      );
    }
    return (
      <select className={common} value={value ?? ''} onChange={(e) => onChange(e.target.value)}>
        <option value="">— pick a value —</option>
        {p.options.map((opt, i) => (
          <option key={`${opt}-${i}`} value={opt}>{opt}</option>
        ))}
      </select>
    );
  }

  const dt = String(p.dataType || '').toUpperCase();
  if (dt === 'DATE') {
    return <input type="date" className={common} value={value ?? ''} onChange={(e) => onChange(e.target.value)} />;
  }
  if (dt === 'INTEGER' || dt === 'FLOAT' || dt === 'NUMBER') {
    return <input type="number" className={common} value={value ?? ''} onChange={(e) => onChange(e.target.value)} />;
  }
  return (
    <input
      type="text"
      className={common}
      value={value ?? ''}
      placeholder={p.defaultValue != null ? `default: ${p.defaultValue}` : ''}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

export default function RunReportModal({ report, onClose }) {
  const [loadingParams, setLoadingParams] = useState(true);
  const [paramsErr, setParamsErr] = useState('');
  const [params, setParams] = useState([]);
  const [values, setValues] = useState({});
  const [format, setFormat] = useState('pdf');
  const [definition, setDefinition] = useState(null); // { supportedFormats, templates, defaultTemplateName }
  const [definitionError, setDefinitionError] = useState('');

  const [running, setRunning] = useState(false);
  const [runErr, setRunErr] = useState('');
  const [runHint, setRunHint] = useState('');
  const [result, setResult] = useState(null); // { mimeType, base64, sizeBytes, filename, previewText, elapsedMs }
  const [downloadUrl, setDownloadUrl] = useState('');

  // Load parameter definitions for this report.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingParams(true);
      setParamsErr('');
      try {
        const res = await API.get(`/api/reports/${report._id}/params`, { validateStatus: () => true });
        if (cancelled) return;
        if (res.status === 200) {
          const list = Array.isArray(res.data.params) ? res.data.params : [];
          setParams(list);
          const seed = {};
          list.forEach((p) => {
            if (p.defaultValue != null && p.defaultValue !== '') seed[p.name] = p.defaultValue;
            else if (p.multiValuesAllowed) seed[p.name] = [];
            else seed[p.name] = '';
          });
          setValues(seed);

          // Definition is best-effort: BIP may restrict it. If we got it,
          // narrow the format options and pick the report's actual default format.
          const defn = res.data.definition || null;
          setDefinition(defn);
          setDefinitionError(res.data.definitionError || '');
          if (defn && Array.isArray(defn.supportedFormats) && defn.supportedFormats.length) {
            const supportedSet = new Set(defn.supportedFormats.map((s) => String(s).toLowerCase()));
            const firstSupportedUi = FORMATS.find((f) =>
              FORMAT_KEYWORD_ALIASES[f.id].some((alias) => supportedSet.has(alias))
            );
            // Prefer the report's default-template default format if it maps to a UI option,
            // otherwise the first UI format that is supported.
            const defTpl = (defn.templates || []).find((t) => t.isDefault) || (defn.templates || [])[0];
            const defKeyword = (defTpl?.defaultFormat || '').toLowerCase();
            const matchedUiFromDefault = FORMATS.find((f) =>
              FORMAT_KEYWORD_ALIASES[f.id].includes(defKeyword)
            );
            if (matchedUiFromDefault) setFormat(matchedUiFromDefault.id);
            else if (firstSupportedUi) setFormat(firstSupportedUi.id);
          }
        } else {
          setParamsErr(res.data?.error || `HTTP ${res.status}`);
        }
      } catch (err) {
        if (!cancelled) setParamsErr(err.message || 'Failed to load parameters');
      } finally {
        if (!cancelled) setLoadingParams(false);
      }
    })();
    return () => { cancelled = true; };
  }, [report._id]);

  // Build a download URL for the result blob (PDF / XLSX / fallback).
  useEffect(() => {
    if (!result?.base64) {
      setDownloadUrl('');
      return undefined;
    }
    const blob = base64ToBlob(result.base64, result.mimeType);
    const url = URL.createObjectURL(blob);
    setDownloadUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [result?.base64, result?.mimeType]);

  async function runReport() {
    setRunning(true);
    setRunErr('');
    setRunHint('');
    setResult(null);
    try {
      // Strip empty optional values; BIP treats absent params as "use default / select all".
      const cleaned = {};
      Object.entries(values).forEach(([name, v]) => {
        if (v === null || v === undefined) return;
        if (Array.isArray(v)) {
          if (v.length) cleaned[name] = v;
        } else if (String(v).length) {
          cleaned[name] = v;
        }
      });
      const res = await API.post(
        `/api/reports/${report._id}/run`,
        { params: cleaned, format },
        { validateStatus: () => true }
      );
      if (res.status === 200 && res.data?.ok) {
        setResult(res.data);
      } else {
        setRunErr(res.data?.error || `HTTP ${res.status}`);
        if (res.data?.hint) setRunHint(res.data.hint);
      }
    } catch (err) {
      setRunErr(err.message || 'Failed to run report');
    } finally {
      setRunning(false);
    }
  }

  // Which UI formats are actually producible by this report?
  // If we couldn't fetch the definition, treat all as enabled.
  const formatSupport = useMemo(() => {
    const supported = new Set((definition?.supportedFormats || []).map((s) => String(s).toLowerCase()));
    const map = {};
    FORMATS.forEach((f) => {
      map[f.id] = supported.size === 0 || FORMAT_KEYWORD_ALIASES[f.id].some((a) => supported.has(a));
    });
    return map;
  }, [definition]);

  const csvTable = useMemo(() => {
    if (!result || result.format !== 'csv' || !result.previewText) return null;
    const rows = parseCsv(result.previewText);
    if (rows.length === 0) return null;
    return rows.slice(0, 200); // cap preview to 200 rows so the modal stays snappy
  }, [result]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-emerald-600">
              <FileSpreadsheet className="h-4 w-4" />
              Run BIP report
            </div>
            <div className="mt-1 truncate text-lg font-semibold text-slate-800">{report.displayName}</div>
            <div className="truncate font-mono text-[11px] text-slate-500">{report.path}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {/* ─── Params ─── */}
          <div className="mb-4">
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Parameters</h3>
            {loadingParams ? (
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading parameter definitions…
              </div>
            ) : paramsErr ? (
              <div className="flex items-start gap-2 rounded-md bg-rose-50 p-2.5 text-xs text-rose-800">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                {paramsErr}
              </div>
            ) : params.length === 0 ? (
              <div className="rounded-md bg-slate-50 p-3 text-xs text-slate-500">
                This report has no input parameters — just pick a format and click <span className="font-medium text-slate-700">Run</span>.
              </div>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                {params.map((p) => (
                  <div key={p.name}>
                    <label className="mb-1 block text-xs font-medium text-slate-600">
                      {p.label || p.name}
                      <span className="ml-1 text-[10px] uppercase tracking-wide text-slate-400">
                        {p.dataType}{p.multiValuesAllowed ? ' · multi' : ''}
                      </span>
                    </label>
                    <ParamField
                      p={p}
                      value={values[p.name]}
                      onChange={(v) => setValues((prev) => ({ ...prev, [p.name]: v }))}
                    />
                    {p.defaultValue != null && p.defaultValue !== '' && (
                      <div className="mt-0.5 text-[10px] text-slate-400">Default: {String(p.defaultValue)}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ─── Format picker ─── */}
          <div className="mb-4">
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
              Output format
              {definition && (
                <span className="ml-2 text-[10px] font-normal lowercase text-slate-400">
                  from this report's template
                </span>
              )}
            </h3>
            <div className="flex flex-wrap gap-1.5">
              {FORMATS.map((f) => {
                const enabled = formatSupport[f.id];
                const selected = format === f.id;
                return (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => enabled && setFormat(f.id)}
                    disabled={!enabled}
                    title={enabled ? '' : "This report's template doesn't produce this format"}
                    className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                      !enabled
                        ? 'cursor-not-allowed border-slate-100 bg-slate-50 text-slate-300'
                        : selected
                        ? 'border-emerald-300 bg-emerald-100 text-emerald-700'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                    }`}
                  >
                    {f.label}
                    {!enabled && <span className="ml-1 text-[9px] uppercase">N/A</span>}
                  </button>
                );
              })}
            </div>
            {definitionError && (
              <div className="mt-1 text-[10px] text-amber-600">
                Couldn't fetch supported formats from BIP — all chips enabled. ({definitionError})
              </div>
            )}
          </div>

          {/* ─── Run errors ─── */}
          {runErr && (
            <div className="mb-3 flex items-start gap-2 rounded-md bg-rose-50 p-2.5 text-sm text-rose-800">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <div>
                <div className="font-medium">Report failed</div>
                <div className="text-xs">{runErr}</div>
                {runHint && <div className="mt-1 text-xs italic text-rose-700">{runHint}</div>}
              </div>
            </div>
          )}

          {/* ─── Result ─── */}
          {result && (
            <div className="rounded-lg border border-slate-200 bg-slate-50">
              <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 px-3 py-2 text-xs text-slate-600">
                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                <span className="font-semibold">Ran in {(result.elapsedMs / 1000).toFixed(1)}s</span>
                <span className="text-slate-400">·</span>
                <span>{result.mimeType}</span>
                <span className="text-slate-400">·</span>
                <span>{fmtSize(result.sizeBytes)}</span>
                <a
                  href={downloadUrl}
                  download={result.filename}
                  className="ml-auto flex items-center gap-1 rounded-md bg-emerald-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-emerald-700"
                >
                  <Download className="h-3.5 w-3.5" />
                  Download {result.filename}
                </a>
              </div>
              <div className="max-h-[40vh] overflow-auto">
                {result.format === 'csv' && csvTable ? (
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-white">
                      <tr>
                        {csvTable[0].map((h, i) => (
                          <th key={i} className="border-b border-slate-200 px-2 py-1.5 text-left font-semibold text-slate-700">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {csvTable.slice(1).map((row, r) => (
                        <tr key={r} className="odd:bg-white even:bg-slate-50">
                          {row.map((cell, c) => (
                            <td key={c} className="border-b border-slate-100 px-2 py-1 text-slate-700">{cell}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : result.format === 'html' && result.previewText ? (
                  <iframe
                    title={`${report.displayName} HTML preview`}
                    className="h-[40vh] w-full bg-white"
                    srcDoc={result.previewText}
                    sandbox=""
                  />
                ) : result.format === 'pdf' && downloadUrl ? (
                  <iframe
                    title={`${report.displayName} PDF preview`}
                    className="h-[40vh] w-full bg-white"
                    src={downloadUrl}
                  />
                ) : result.previewText ? (
                  <pre className="whitespace-pre-wrap break-words p-3 text-[11px] text-slate-700">{result.previewText}</pre>
                ) : (
                  <div className="flex items-center gap-2 p-4 text-xs text-slate-500">
                    <FileText className="h-4 w-4" />
                    Binary output — use the Download button above.
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-slate-200 bg-slate-50 px-5 py-3">
          <div className="text-xs text-slate-500">
            Runs against the active HCM connection via BIP <code className="text-[10px]">PublicReportService</code>.
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100"
            >
              Close
            </button>
            <button
              type="button"
              onClick={runReport}
              disabled={running || loadingParams || !!paramsErr}
              className="flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:bg-slate-300"
            >
              {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
              {running ? 'Running…' : 'Run report'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
